import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import {
  PredictiveEventWindow,
  PredictiveEventType,
  ProbabilityLevel,
  V519PresentationMode,
  V519SubscriptionTier
} from '../../types/v519';
import { EVENT_RULE_REGISTRY } from '../../utils/v519/eventPredictionEngine';
import { predictCanonicalLifeEvents as predictAllLifeEvents } from '../../utils/dashaGocharSynthesisEngine';
import { formatDate } from '../../utils/dateUtils';
import {
  Calendar,
  Clock,
  Sparkles,
  ShieldAlert,
  ChevronRight,
  TrendingUp,
  Award,
  BookOpen,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Lock,
  Compass,
  Filter,
  BarChart2,
  Layers
} from 'lucide-react';

export interface PredictiveEventTimelineCardProps {
  kundali: CompleteKundali;
  predictiveEvents?: readonly PredictiveEventWindow[];
  selectedEventType?: PredictiveEventType;
  onSelectEventType?: (eventType: PredictiveEventType) => void;
  lang: Language;
  presentationMode?: V519PresentationMode;
  userEntitlement?: V519SubscriptionTier | 'FREE' | 'BASIC' | 'PREMIUM' | 'PRO_ASTROLOGER';
  onOpenEvidenceDrawer?: (eventType: PredictiveEventType, ruleId?: string) => void;
  onOpenConsultation?: (prompt: string) => void;
}

const PROBABILITY_STYLES: Record<ProbabilityLevel, {
  bg: string;
  border: string;
  text: string;
  badgeBg: string;
  labelEn: string;
  labelHi: string;
}> = {
  VERY_HIGH: {
    bg: 'bg-emerald-50/80',
    border: 'border-emerald-200',
    text: 'text-emerald-900',
    badgeBg: 'bg-emerald-600 text-white',
    labelEn: 'Very High Activation (78%+)',
    labelHi: 'अति-उच्च सक्रियता (७८%+)'
  },
  HIGH: {
    bg: 'bg-teal-50/80',
    border: 'border-teal-200',
    text: 'text-teal-900',
    badgeBg: 'bg-teal-700 text-white',
    labelEn: 'High Activation (65–77%)',
    labelHi: 'उच्च सक्रियता (६५–७७%)'
  },
  MODERATE: {
    bg: 'bg-amber-50/80',
    border: 'border-amber-200',
    text: 'text-amber-900',
    badgeBg: 'bg-amber-600 text-white',
    labelEn: 'Moderate Activation (48–64%)',
    labelHi: 'मध्यम सक्रियता (४८–६४%)'
  },
  LOW: {
    bg: 'bg-astro-secondary-surface/80',
    border: 'border-astro-border',
    text: 'text-astro-secondary-text',
    badgeBg: 'bg-astro-secondary text-white',
    labelEn: 'Low Activation (<48%)',
    labelHi: 'निम्न सक्रियता (<४८%)'
  }
};

export const PredictiveEventTimelineCard: React.FC<PredictiveEventTimelineCardProps> = ({
  kundali,
  predictiveEvents,
  selectedEventType,
  onSelectEventType,
  lang,
  presentationMode = 'PRO',
  userEntitlement = 'PREMIUM',
  onOpenEvidenceDrawer,
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';
  const isBasic = presentationMode === 'BASIC';
  const isLearner = (presentationMode as string) === 'LEARNER';
  const isPro = presentationMode === 'PRO';

  // Compute all events deterministically if not provided
  const computedEvents = useMemo(() => {
    if (predictiveEvents && predictiveEvents.length > 0) {
      return predictiveEvents;
    }
    return predictAllLifeEvents(kundali, 36);
  }, [kundali, predictiveEvents]);

  const [internalSelectedType, setInternalSelectedType] = useState<PredictiveEventType>(() => {
    return selectedEventType || computedEvents[0]?.eventType || 'CAREER_PROMOTION';
  });

  const activeEventType = selectedEventType || internalSelectedType;
  const activeEvent = useMemo(() => {
    return computedEvents.find(e => e.eventType === activeEventType) || computedEvents[0];
  }, [computedEvents, activeEventType]);

  const handleSelectType = (type: PredictiveEventType) => {
    setInternalSelectedType(type);
    if (onSelectEventType) {
      onSelectEventType(type);
    }
  };

  // Entitlement limits
  const isFreeTier = userEntitlement === 'FREE';
  const maxEventsAllowed = isFreeTier ? 3 : computedEvents.length;
  const visibleEvents = computedEvents.slice(0, maxEventsAllowed);

  if (!activeEvent) {
    return (
      <div className="p-6 bg-astro-bg border border-astro-border rounded-2xl text-center text-astro-secondary-text">
        {isHi ? 'कोई भविष्यवाणी योग्य घटना उपलब्ध नहीं है।' : 'No predictive event windows available.'}
      </div>
    );
  }

  const probMeta = PROBABILITY_STYLES[activeEvent.probability] || PROBABILITY_STYLES.MODERATE;

  return (
    <section
      id="predictive-event-timeline-card"
      aria-labelledby="predictive-event-heading"
      className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-6 shadow-xs space-y-6 text-astro-text"
    >
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-astro-border pb-4">
        <div className="flex items-start sm:items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-astro-primary text-astro-accent flex items-center justify-center shrink-0 shadow-xs">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 id="predictive-event-heading" className="text-base sm:text-lg font-bold text-astro-primary font-serif">
                {isHi ? 'सटीक वैदिक घटना समय एवं संभावना खिड़की' : 'Discrete Vedic Event Timing & Probability Windows'}
              </h3>
              <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
                V5.16 Engine
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text mt-0.5">
              {isHi
                ? 'विंशोत्तरी महादशा, गोचर दृष्टि एवं सर्वाष्टकवर्ग बिंदु का गणितीय काल-संश्लेषण'
                : 'Deterministic multi-cycle synthesis of Vimshottari Dasha, major planetary transits & SAV bindus'}
            </p>
          </div>
        </div>

        {/* View Mode Indicator Badge */}
        <div className="flex items-center gap-2 self-start md:self-center">
          <span className="text-[11px] font-medium text-astro-secondary-text">
            {isHi ? 'दृष्टिकोण:' : 'View Mode:'}
          </span>
          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-astro-card border border-astro-border text-astro-primary">
            {isBasic ? (isHi ? 'सरल / बेसिक' : 'Basic View') : isLearner ? (isHi ? 'जिज्ञासु / लर्नर' : 'Learner View') : (isHi ? 'विद्वान / प्रो' : 'Pro Astrologer')}
          </span>
        </div>
      </div>

      {/* Event Selection Pills */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'प्रमुख जीवन घटनाएं (३६ मास पूर्वानुमान)' : 'Select Life Event (36-Month Horizon)'}</span>
          </label>
          {isFreeTier && (
            <span className="text-[11px] font-medium text-amber-700 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {isHi ? 'मुफ्त संस्करण में शीर्ष ३ घटनाएं' : 'Free tier: Top 3 events shown'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {visibleEvents.map((evt) => {
            const isSelected = evt.eventType === activeEventType;
            const pStyle = PROBABILITY_STYLES[evt.probability];

            return (
              <button
                key={evt.eventId}
                id={`event-pill-${evt.eventType}`}
                onClick={() => handleSelectType(evt.eventType)}
                className={`px-3 py-2 rounded-xl text-xs font-medium border whitespace-nowrap transition-all flex items-center gap-2 touch-manipulation cursor-pointer ${
                  isSelected
                    ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                    : 'bg-astro-card text-astro-text border-astro-border hover:border-astro-accent'
                }`}
              >
                <span>{isHi ? evt.eventTitleHi : evt.eventTitleEn}</span>
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    isSelected ? 'bg-white/20 text-white' : pStyle.badgeBg
                  }`}
                >
                  {evt.confidenceScore}%
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Event Showcase Card */}
      <div className={`p-4 sm:p-5 rounded-xl border ${probMeta.border} ${probMeta.bg} space-y-4`}>
        {/* Title & Probability Banner */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-black/10 pb-3">
          <div>
            <h4 className="text-base sm:text-lg font-bold text-astro-primary font-serif flex items-center gap-2">
              <span>{isHi ? activeEvent.eventTitleHi : activeEvent.eventTitleEn}</span>
            </h4>
            <div className="flex items-center gap-2 text-xs text-astro-secondary-text mt-0.5">
              <span>{isHi ? 'घटना श्रेणी:' : 'Domain:'} <strong className="text-astro-text">{activeEvent.eventType}</strong></span>
              <span>•</span>
              <span>{isHi ? 'गणना समय:' : 'Calculated:'} {formatDate(activeEvent.calculatedAt)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <div className="text-right">
              <div className="text-[11px] text-astro-secondary-text font-medium">
                {isHi ? 'संयुक्त संभावना' : 'Composite Probability'}
              </div>
              <div className="text-sm sm:text-base font-bold text-astro-primary">
                {isHi ? probMeta.labelHi : probMeta.labelEn}
              </div>
            </div>
            <div className={`px-3 py-1.5 rounded-xl text-sm font-extrabold ${probMeta.badgeBg} shadow-xs`}>
              {activeEvent.confidenceScore}%
            </div>
          </div>
        </div>

        {/* Timeline Visualization: Start -> Peak Window -> End */}
        <div className="bg-white/90 border border-astro-border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between text-xs font-bold text-astro-primary">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-astro-accent" />
              {isHi ? 'सक्रियण काल चक्र (सटीक खिड़की)' : 'Planetary Timing Window'}
            </span>
            <span className="text-[11px] font-normal text-astro-secondary-text">
              {isHi ? 'सर्वोत्तम प्रभाव anchor: शिखर काल' : 'Optimal peak anchor point'}
            </span>
          </div>

          {/* Timeline Bar */}
          <div className="relative pt-2 pb-1">
            <div className="h-3 w-full bg-astro-card rounded-full overflow-hidden border border-astro-border relative">
              <div
                className="h-full bg-gradient-to-r from-teal-500 via-astro-accent to-teal-700 rounded-full"
                style={{ width: `${Math.max(25, activeEvent.confidenceScore)}%` }}
              />
            </div>
          </div>

          {/* Date Milestones Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs pt-1">
            <div className="p-2 rounded-lg bg-astro-card border border-astro-border">
              <div className="text-[10px] uppercase font-semibold text-astro-secondary-text">
                {isHi ? 'प्रारंभ काल (Start)' : 'Window Opens'}
              </div>
              <div className="font-bold text-astro-text text-sm mt-0.5">
                {formatDate(activeEvent.startDate)}
              </div>
              <div className="text-[10px] text-astro-secondary-text">
                {isHi ? 'सक्रियता का आरंभ' : 'Initial activation'}
              </div>
            </div>

            <div className="p-2 rounded-lg bg-astro-primary/10 border border-astro-primary/30 ring-1 ring-astro-primary/20">
              <div className="text-[10px] uppercase font-bold text-astro-primary flex items-center justify-between">
                <span>{isHi ? 'शिखर काल (Peak Window)' : 'Peak Activation'}</span>
                <Sparkles className="w-3 h-3 text-astro-accent" />
              </div>
              <div className="font-extrabold text-astro-primary text-sm mt-0.5">
                {formatDate(activeEvent.peakDate)}
              </div>
              <div className="text-[10px] text-astro-primary font-medium">
                {isHi ? '४५-दिवसीय तीव्र फल काल' : 'Highest astrological convergence'}
              </div>
            </div>

            <div className="p-2 rounded-lg bg-astro-card border border-astro-border">
              <div className="text-[10px] uppercase font-semibold text-astro-secondary-text">
                {isHi ? 'समापन काल (End)' : 'Window Closes'}
              </div>
              <div className="font-bold text-astro-text text-sm mt-0.5">
                {formatDate(activeEvent.endDate)}
              </div>
              <div className="text-[10px] text-astro-secondary-text">
                {isHi ? 'ऊर्जा स्थिरीकरण' : 'Transition period'}
              </div>
            </div>
          </div>
        </div>

        {/* Basic View: Simple Takeaway */}
        {isBasic && (
          <div className="p-3.5 bg-white/95 rounded-xl border border-astro-border space-y-2">
            <h5 className="text-xs font-bold text-astro-primary uppercase tracking-wider flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>{isHi ? 'सरल सारांश एवं निष्कर्ष' : 'Key Takeaway for You'}</span>
            </h5>
            <p className="text-xs sm:text-sm text-astro-text leading-relaxed">
              {isHi
                ? `आपकी कुंडली के अनुसार, ${activeEvent.eventTitleHi} के लिए सबसे अनुकूल समय ${formatDate(activeEvent.startDate)} से ${formatDate(activeEvent.endDate)} के मध्य है, जिसका मुख्य प्रभाव ${formatDate(activeEvent.peakDate)} के आस-पास रहेगा।`
                : `According to your astrological cycles, the most favorable window for ${activeEvent.eventTitleEn} spans between ${formatDate(activeEvent.startDate)} and ${formatDate(activeEvent.endDate)}, with peak planetary support centering around ${formatDate(activeEvent.peakDate)}.`}
            </p>
          </div>
        )}

        {/* Learner & Pro View: Progressive Astrological Evidence */}
        {(isLearner || isPro) && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="text-xs font-bold text-astro-primary uppercase tracking-wider flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-astro-accent" />
                <span>{isHi ? 'शास्त्रीय प्रमाण एवं सक्रियता घटक' : 'Astrological Evidence Streams'}</span>
              </h5>
              {isPro && onOpenEvidenceDrawer && (
                <button
                  onClick={() => onOpenEvidenceDrawer(activeEvent.eventType)}
                  className="text-xs text-astro-primary hover:text-astro-accent font-bold flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  <span>{isHi ? 'शास्त्रीय सूत्र देखें' : 'View Classical Sutras'}</span>
                  <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              {/* Parashari Dasha Stream */}
              <div className="p-3 bg-white/95 rounded-xl border border-astro-border space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-astro-primary">
                    {isHi ? '१. विंशोत्तरी दशा चक्र' : '1. Parashari Vimshottari Dasha'}
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-astro-primary/10 text-astro-primary">
                    {activeEvent.triangulationEvidence.parashariDasha.mahadashaLord}–{activeEvent.triangulationEvidence.parashariDasha.antardashaLord}
                  </span>
                </div>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? activeEvent.triangulationEvidence.parashariDasha.explanationHi
                    : activeEvent.triangulationEvidence.parashariDasha.explanationEn}
                </p>
                {isPro && (
                  <div className="text-[11px] text-astro-text pt-1 flex items-center gap-2">
                    <span>{isHi ? 'सक्रिय भाव:' : 'Active Houses:'}</span>
                    <span className="font-mono font-bold text-astro-primary">
                      {activeEvent.triangulationEvidence.parashariDasha.activeHouses.join(', ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Transit & SAV Stream */}
              <div className="p-3 bg-white/95 rounded-xl border border-astro-border space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-astro-primary">
                    {isHi ? '२. गोचर एवं सर्वाष्टकवर्ग' : '2. Transit & SAV Modulation'}
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-astro-accent/20 text-astro-accent">
                    SAV: {activeEvent.triangulationEvidence.sav.savBindus} {isHi ? 'बिंदु' : 'Bindus'}
                  </span>
                </div>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? activeEvent.triangulationEvidence.parashariTransit.explanationHi
                    : activeEvent.triangulationEvidence.parashariTransit.explanationEn}
                </p>
                {isPro && (
                  <div className="text-[11px] text-astro-text pt-1 flex items-center justify-between">
                    <span>
                      {isHi ? 'गुरु गोचर भाव:' : 'Jupiter Transit House:'}{' '}
                      <strong className="text-astro-primary">H{activeEvent.triangulationEvidence.parashariTransit.jupiterTransitHouse}</strong>
                    </span>
                    <span>
                      {isHi ? 'शनि गोचर भाव:' : 'Saturn Transit House:'}{' '}
                      <strong className="text-astro-primary">H{activeEvent.triangulationEvidence.parashariTransit.saturnTransitHouse}</strong>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recommended Action & Remedial Window */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className="p-3.5 bg-white/95 rounded-xl border border-astro-border space-y-1">
            <div className="font-bold text-astro-primary flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-teal-600" />
              <span>{isHi ? 'अनुशंसित क्रियात्मक कदम' : 'Recommended Strategic Action'}</span>
            </div>
            <p className="text-astro-secondary-text leading-relaxed">
              {isHi ? activeEvent.recommendedActionHi : activeEvent.recommendedActionEn}
            </p>
          </div>

          <div className="p-3.5 bg-white/95 rounded-xl border border-astro-border space-y-1">
            <div className="font-bold text-astro-primary flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'शास्त्रीय उपाय एवं अनुकूल समय' : 'Remedial Prep Window'}</span>
            </div>
            <p className="text-astro-secondary-text leading-relaxed">
              {isHi ? activeEvent.remedialWindowHi : activeEvent.remedialWindowEn}
            </p>
          </div>
        </div>

        {/* AI Consultation Prompt Action */}
        {onOpenConsultation && (
          <div className="pt-1 flex justify-end">
            <button
              onClick={() => {
                const prompt = isHi
                  ? `कृपया मेरी कुंडली में ${activeEvent.eventTitleHi} के लिए ${formatDate(activeEvent.startDate)} से ${formatDate(activeEvent.endDate)} की अवधि के ज्योतिषीय विश्लेषण और उपायों पर विस्तृत मार्गदर्शन प्रदान करें।`
                  : `Please provide in-depth consultation on the astrological indications and remedies for ${activeEvent.eventTitleEn} during the window of ${formatDate(activeEvent.startDate)} to ${formatDate(activeEvent.endDate)}.`;
                onOpenConsultation(prompt);
              }}
              className="px-3.5 py-2 rounded-xl bg-astro-primary hover:bg-[#123D3C] text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
            >
              <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'एआई आचार्य से इस घटना पर परामर्श करें' : 'Consult AI Acharya on this Event'}</span>
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Mandatory Astrological Disclaimer */}
        <div className="p-2.5 rounded-lg bg-astro-card border border-astro-border text-[11px] text-astro-secondary-text flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <p className="leading-snug">
            {isHi ? activeEvent.limitationsDisclaimerHi : activeEvent.limitationsDisclaimerEn}
          </p>
        </div>
      </div>
    </section>
  );
};
